import { ColDef, GridApi, ICellRendererParams } from "ag-grid-community"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import TextFiledCellRender from "../contacts/textFieldCellRender"
import { emptyLabels } from "../shared/constants"
import GridComponent from "../shared/grid"
import { ImportEditableRow, ImportFieldKey } from "./import-numbers-shared.component"

interface IProps {
    Rows: ImportEditableRow[]
    ErrorRows: ImportEditableRow[]
    OnRowsChange: (rows: ImportEditableRow[]) => void
    GetRowErrors: (rows: ImportEditableRow[]) => ImportEditableRow[]
}

const ImportNumbersGrid = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>()
    const rowsRef = useRef(props.Rows)
    const propsRef = useRef(props)
    const errorRowsRef = useRef(props.ErrorRows)

    useEffect(() => {
        rowsRef.current = props.Rows
    }, [props.Rows])

    useEffect(() => {
        errorRowsRef.current = props.ErrorRows
    }, [props.ErrorRows])

    useEffect(() => {
        propsRef.current = props
    })

    const errorAfterEdit = useCallback((rowId: string, field: ImportFieldKey, nextValue: string) => {
        const nextRows = rowsRef.current.map((row) => row.RowId === rowId ? { ...row, [field]: nextValue } : row)
        return propsRef.current.GetRowErrors(nextRows).find((row) => row.RowId === rowId)?.[field] ?? ""
    }, [])

    const applyField = useCallback((rowId: string, field: ImportFieldKey, nextValue: string) => {
        const nextRows = rowsRef.current.map((row) => row.RowId === rowId ? { ...row, [field]: nextValue } : row)
        rowsRef.current = nextRows
        propsRef.current.OnRowsChange(nextRows)
    }, [])

    const errorFor = (rowId: string, field: ImportFieldKey) =>
        errorRowsRef.current.find((row) => row.RowId === rowId)?.[field] ?? ""

    const renderTextField = useCallback((params: ICellRendererParams, field: ImportFieldKey) => {
        const row = params.data as ImportEditableRow
        return (
            <TextFiledCellRender
                Value={(params.value || "").toString()}
                Error={errorFor(row.RowId, field)}
                OnCommit={(nextValue) => applyField(row.RowId, field, nextValue)}
                OnValidateWhileTyping={(nextValue) => errorAfterEdit(row.RowId, field, nextValue)}
            />
        )
    }, [applyField, errorAfterEdit])

    const columnDefs: ColDef[] = useMemo(() => [
        {
            field: "Name",
            headerName: "Name",
            minWidth: 240,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => renderTextField(params, "Name"),
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "Number",
            headerName: "Phone Number",
            minWidth: 260,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => renderTextField(params, "Number"),
            cellStyle: { display: "flex", alignItems: "center" }
        }
    ], [renderTextField])

    return (
        <div className="mb-1 h-45 import-users-grid">
            <GridComponent
                ColumnDefs={columnDefs}
                RowData={props.Rows}
                OnGridReady={(params) => setGridApi(params.api)}
                IsPaginationEnabled={true}
                EmptyLabel={emptyLabels.PHONE_NUMBER}
                LockVisible={false}
                isHorizontalScroll={true}
                GetRowId={(params) => String((params.data as ImportEditableRow).RowId)}
                EnableCellChangeFlash={false}
                RowHeight={65}
                gridApi={gridApi}
            />
        </div>
    )
}

export default ImportNumbersGrid
